import { toastController } from '@ionic/vue'
import { useI18n } from 'vue-i18n'

// Shared active toasts map for stack tracking
const activeToasts: Map<string, any[]> = new Map()

export function useToast() {
	const { t } = useI18n()

	const MINIMUM_DESKTOP_WIDTH = 768
	const CSS_MEDIA_BREAKPOINT = 1354 // CSS uses @media screen and (min-width: 1354px)
	
	/**
	 * Generates a unique key for toast position tracking
	 */
	function getPositionKey(verticalPosition: string, horizontalPosition: string): string {
		return `${verticalPosition}-${horizontalPosition}`
	}

	const TOAST_OFFSET = 60 // Offset v px medzi toast hláškami
	
	/**
	 * Registers a toast in the active toasts map
	 */
	function registerToast(toast: any, verticalPosition: string, horizontalPosition: string): void {
		const key = getPositionKey(verticalPosition, horizontalPosition)
		const toasts = activeToasts.get(key) || []
		toasts.push(toast)
		activeToasts.set(key, toasts)
	}
	
	/**
	 * Updates toast position in DOM
	 */
	function updateToastPosition(toastElement: HTMLElement, index: number, verticalPosition: string, horizontalPosition?: string): void {
		if (!toastElement) return
		
		const offset = index * TOAST_OFFSET
		toastElement.classList.add('w-toast-stack')
		
		// Remove any existing bottom style for top/middle positions
		if (verticalPosition !== 'bottom') {
			toastElement.style.bottom = ''
		}
		
		// Check if this is a center toast - check both class and parameter
		const hasWCenterClass = toastElement.classList.contains('w-center')
		const isCenterByParam = horizontalPosition === 'center'
		// Check if screen is smaller than CSS media breakpoint - on smaller screens, CSS position rules don't apply and toasts become center
		const isSmallScreen = window.innerWidth < CSS_MEDIA_BREAKPOINT
		// On small screens (< 1354px), CSS position rules don't apply, so all toasts are effectively center
		const isCenter = hasWCenterClass || isCenterByParam || isSmallScreen
		
		if (verticalPosition === 'top') {
			// For top position, move down from 50%
			toastElement.style.top = `calc(50% + ${offset}px)`
		} else if (verticalPosition === 'bottom') {
			// For bottom position, move up from 50% (use top with negative offset)
			toastElement.style.top = `calc(50% - ${offset}px)`
		} else if (verticalPosition === 'middle') {
			// For middle position, move down from 4% + 4px
			toastElement.style.top = `calc(4% + 4px + ${offset}px)`
		}
		
		// For center toasts, use translate(0%, -50%) since left: 50% already centers horizontally
		// For other toasts, use translate(-50%, -50%) for proper centering
		const transformValue = isCenter ? 'translate(0%, -50%)' : 'translate(-50%, -50%)'
		toastElement.style.transform = transformValue
	}

	/**
	 * Unregisters a toast from the active toasts map and updates positions of remaining toasts
	 */
	function unregisterToast(toast: any, verticalPosition: string, horizontalPosition: string): void {
		const key = getPositionKey(verticalPosition, horizontalPosition)
		const toasts = activeToasts.get(key) || []
		const index = toasts.indexOf(toast)
		
		if (index !== -1) {
			toasts.splice(index, 1)
			activeToasts.set(key, toasts)
			
			// Update positions of remaining toasts
			const positionClass = `w-${verticalPosition}`
			setTimeout(() => {
				const toastElements = document.querySelectorAll(`ion-toast.${positionClass}.w-toast-stack`) as NodeListOf<HTMLElement>
				toasts.forEach((remainingToast, newIndex) => {
					if (toastElements[newIndex]) {
						updateToastPosition(toastElements[newIndex], newIndex, verticalPosition, horizontalPosition)
					}
				})
			}, 50)
		}
	}

	/**
	 * Creates a toast with stack support
	 */
	async function createToast(options: any, verticalPosition: string, horizontalPosition: string): Promise<void> {
		const toast = await toastController.create(options)
		
		// Register toast before presenting
		registerToast(toast, verticalPosition, horizontalPosition)
		
		// Handle toast dismissal
		toast.onDidDismiss().then(() => {
			unregisterToast(toast, verticalPosition, horizontalPosition)
		})

		await toast.present()
		
		// Update CSS after toast is in DOM - wait a bit for Ionic to render
		const positionClass = `w-${verticalPosition}`
		const key = getPositionKey(verticalPosition, horizontalPosition)
		const toasts = activeToasts.get(key) || []
		const index = toasts.length - 1
		
		// Try multiple times to find the toast element (Ionic might need time to render)
		const updatePosition = (attempt = 0) => {
			// Find all toasts with the position class and update the one at our index
			const toastElements = Array.from(document.querySelectorAll(`ion-toast.${positionClass}`)) as HTMLElement[]
			
			// Update all toasts in the stack to ensure correct positioning
			toasts.forEach((_, idx) => {
				if (toastElements[idx]) {
					updateToastPosition(toastElements[idx], idx, verticalPosition, horizontalPosition)
				}
			})
			
			// If we didn't find the element yet, try again
			if (toastElements.length === 0 && attempt < 10) {
				setTimeout(() => updatePosition(attempt + 1), 100)
			}
		}
		
		setTimeout(() => updatePosition(), 150)
	}

	function parseAndTranslateError(data) {
		if (typeof data?.response?.data?.error == 'string') {
			return [t(data.response.data.error)]
		}

		if (typeof data == 'string') {
			return [t(data)]
		}

		if (data && data.error && typeof data.error == 'string') {
			return [t(data.error)]
		}

		if (data && data.error && typeof data.error == 'object') {
			return Object.values(t(data.error))
		}

		if (data.data && data.data.error && typeof data.data.error == 'string') {
			return [t(data.data.error)]
		}

		if (data && data.data && data.data.error && typeof data.data.error == 'object') {
			return Object.values(data.data.error).map((error) => t(error[0]) + ' ')
		}

		if (data.statusText) {
			return [t(data.statusText)]
		}

		return [data].map((error) => t(error))
	}

	async function error(error: any = 'Error', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await createToast({
			message: parseAndTranslateError(error?.response || error),
			color: 'danger',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: 5000
		}, verticalPosition, horizontalPosition)
	}

	async function success(message: any = 'Success', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await createToast({
			message: parseAndTranslateError(message),
			color: 'success',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: 5000
		}, verticalPosition, horizontalPosition)
	}

	async function warning(message: any = 'Warning', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await createToast({
			message: parseAndTranslateError(message),
			color: 'warning',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: 5000
		}, verticalPosition, horizontalPosition)
	}

	async function info(message: any = 'Info', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await createToast({
			message: parseAndTranslateError(message),
			color: 'primary',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: 5000
		}, verticalPosition, horizontalPosition)
	}

	async function custom(options) {
		const verticalPosition = options.position || 'top'
		const horizontalPosition = options.cssClass?.includes('w-left') ? 'left' : options.cssClass?.includes('w-center') ? 'center' : 'right'
		await createToast(options, verticalPosition, horizontalPosition)
	}

	return {
		parseAndTranslateError,
		error,
		success,
		warning,
		info,
		custom
	}
}
